import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Bell, CheckCheck, ChevronDown, Mail, MailOpen, Trash2 } from "lucide-react";
import { Button } from "../../ui/button";
import { useNavigate } from "react-router-dom";
import NotificationsPanelContent from "./NotificationsPanelContent";
import { getCloudAlertsPath } from "../CloudDashboard/lib/cloudRoutes";
import { selectCloudAccount } from "../cloudAccountSelection";
import type { AdminNotification } from "./Api/adminNotificationApi";
import useAdminNotifications from "./useAdminNotifications";
import useEndpointSeverityAlerts from "./useEndpointSeverityAlerts";
import "./NotificationsDropdown.css";

const RESOURCE_REQUESTS_PATH = "/resourceRequests";

type NotificationSection = "alerts" | "requests";

export default function NotificationsDropdown() {
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState<NotificationSection>("alerts");
  const {
    notifications,
    loaded,
    unseenCount,
    unreadCount,
    requestUnreadCount,
    hasCriticalUnread,
    clearableCount,
    sessionNewIds,
    markRead,
    markUnread,
    lock,
    unlock,
    remove,
    markAllRead,
    clearAll,
  } = useAdminNotifications(open);
  const endpointAlerts = useEndpointSeverityAlerts(open);
  const navigate = useNavigate();
  const requestBadgeText = requestUnreadCount > 99 ? "99+" : String(requestUnreadCount);
  const alertNotifications = useMemo(
    () => notifications.filter((notification) => {
      if (notification.category === "RESOURCE_REQUEST") return false;
      if (notification.category !== "CLOUD_ALERT") return true;
      const severity = notification.severity?.toUpperCase();
      return severity === "CRITICAL" || severity === "HIGH";
    }),
    [notifications],
  );
  const notificationCount = useMemo(
    () => alertNotifications.filter((notification) => notification.status === "UNSEEN" || notification.status === "SEEN").length,
    [alertNotifications],
  );
  const requestNotifications = useMemo(
    () => notifications.filter((notification) => notification.category === "RESOURCE_REQUEST"),
    [notifications],
  );
  const sections = [
    { id: "alerts" as const, label: "Alerts", icon: Bell, count: notificationCount },
    { id: "requests" as const, label: "Resource requests", icon: Mail, count: requestUnreadCount },
  ];

  const handleViewDetails = (notification: AdminNotification) => {
    const unread = notification.status === "UNSEEN" || notification.status === "SEEN";
    if (notification.category === "RESOURCE_REQUEST") {
      if (unread) markRead(notification.id);
      setOpen(false);
      navigate(notification.actionUrl || RESOURCE_REQUESTS_PATH, {
        state: { resourceRequestPlatform: notification.metadata?.platform },
      });
      return;
    }
    if (notification.category !== "CLOUD_ALERT" || !notification.cloudType || !notification.alertId) return;
    if (notification.cloudAccountId) selectCloudAccount(notification.cloudType, notification.cloudAccountId);
    if (unread) markRead(notification.id);
    setOpen(false);
    navigate(getCloudAlertsPath(notification.cloudType), {
      state: {
        notificationAlert: {
          alertId: notification.alertId,
          cloudAccountId: notification.cloudAccountId,
        },
      },
    });
  };
  const badgeText = notificationCount > 99 ? "99+" : String(notificationCount);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const panelId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const header = panelRef.current?.closest(".dashboard-top-card");
    if (!header) return;

    const handlePointerEnter = () => setHovered(true);
    const handlePointerLeave = () => setHovered(false);
    header.addEventListener("pointerenter", handlePointerEnter);
    header.addEventListener("pointerleave", handlePointerLeave);
    return () => {
      header.removeEventListener("pointerenter", handlePointerEnter);
      header.removeEventListener("pointerleave", handlePointerLeave);
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        !buttonRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <div
      className="notifications-panel-viewport"
      data-emphasized={hovered || focused || open}
      data-alert={unreadCount > 0 && !open}
    >
      <div
        ref={panelRef}
        className={`notifications-panel${open ? " is-open" : ""}`}
        data-critical={hasCriticalUnread}
      >
        <div
          id={panelId}
          role="region"
          aria-label="Notifications"
          aria-hidden={!open}
          className="nt-panel"
          {...(open ? {} : { inert: "" })}
        >
          <div className="nt-header">
            <h2 className="nt-heading">Notifications</h2>
            <div className="nt-tabs" role="tablist" aria-label="Notification sections">
              {sections.map(({ id, label, icon: Icon, count }) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  className="nt-tab"
                  aria-selected={section === id}
                  onClick={() => setSection(id)}
                >
                  <Icon aria-hidden="true" />
                  {label}
                  {count > 0 && <span className="nt-tab-count">{count > 99 ? "99+" : count}</span>}
                </button>
              ))}
            </div>
            <span className="nt-summary">
              <strong>{unseenCount}</strong> new · <strong>{notifications.length}</strong> in the last 7 days
            </span>
          </div>
          <NotificationsPanelContent
            key={section}
            notifications={section === "alerts" ? alertNotifications : requestNotifications}
            emptyText={section === "alerts" ? "No alerts in the last 7 days." : "No resource requests in the last 7 days."}
            loaded={loaded}
            sessionNewIds={sessionNewIds}
            onMarkRead={markRead}
            onMarkUnread={markUnread}
            onLock={lock}
            onUnlock={unlock}
            onRemove={remove}
            onViewDetails={handleViewDetails}
            endpointAlerts={section === "alerts" ? endpointAlerts : []}
            onViewEndpointAlerts={() => {
              setOpen(false);
              navigate("/dashboard/alerts");
            }}
          />
          <div className="nt-footer">
            <span className="nt-footer-count">
              <strong>{unreadCount}</strong> unread of <strong>{notifications.length}</strong> notification{notifications.length === 1 ? "" : "s"}
            </span>
            <button
              type="button"
              className="nt-clear-button"
              data-tone="primary"
              onClick={markAllRead}
              disabled={unreadCount === 0}
            >
              <CheckCheck aria-hidden="true" />
              Mark all read
            </button>
            <button
              type="button"
              className="nt-clear-button"
              onClick={clearAll}
              disabled={clearableCount === 0}
            >
              <Trash2 aria-hidden="true" />
              Clear all
            </button>
          </div>
        </div>
        <Button
          ref={buttonRef}
          type="button"
          variant="ghost"
          className="notifications-surface-trigger no-default-hover-elevate no-default-active-elevate"
          aria-label={open ? "Close notifications" : "Open notifications"}
          aria-expanded={open}
          aria-controls={panelId}
          onFocus={(event) => setFocused(event.currentTarget.matches(":focus-visible"))}
          onBlur={() => setFocused(false)}
          onClick={(event) => {
            if (!open) {
              const target = event.target as HTMLElement;
              if (target.closest(".notifications-requests")) setSection("requests");
              else if (target.closest(".notifications-label")) setSection("alerts");
            }
            setOpen((value) => !value);
          }}
        >
          <span className="notifications-cluster">
            <span
              className="notifications-requests"
              data-has-count={requestUnreadCount > 0}
              title="Resource requests"
              aria-hidden="true"
            >
              <span className="notifications-mail-icon">
                <Mail className="notifications-mail-closed" />
                <MailOpen className="notifications-mail-open" />
              </span>
              {requestUnreadCount > 0 && (
                <span className="notifications-requests-count">{requestBadgeText}</span>
              )}
            </span>
            <span
              className="notifications-label"
              data-has-count={notificationCount > 0}
              title="Alerts"
            >
              <Bell aria-hidden="true" />
              {notificationCount > 0 && (
                <span className="notifications-badge" aria-hidden="true">
                  {badgeText}
                </span>
              )}
            </span>
          </span>
        </Button>
        <div className="notifications-trigger-slot">
          <Button
            type="button"
            variant="default"
            className="notifications-trigger no-default-hover-elevate no-default-active-elevate"
            data-visible={open}
            tabIndex={-1}
            aria-hidden="true"
            aria-expanded={open}
            onPointerDown={(event) => event.preventDefault()}
            onClick={() => setOpen((value) => !value)}
          >
            <ChevronDown aria-hidden="true" strokeWidth={2.5} />
          </Button>
        </div>
      </div>
    </div>
  );
}
